import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Base, ReverLoading } from '@itsrever/design-system'
import { PbECommerceSettingsPlatformEnum } from '@itsrever/rever-api'
import {
  useExtraDetailsTitles,
  useGroupExchanges,
  useSaveExtraDetailsTitles
} from '@/application/manage-exchanges'
import { useScopePortals } from '@/application/queries'
import { useFullCatalogPayments } from '@/application/manage-group-settings'
import { useReturnsAndRefundsV2 } from '@/hooks/use-returns-and-refunds-v2'
import {
  DEFAULT_FULL_CATALOG,
  DEFAULT_INSTANT_EXCHANGE,
  DEFAULT_ORDERS,
  DEFAULT_POLICY,
  ExchangePolicy,
  ExchangesKind,
  FullCatalog,
  Layered
} from '@/domain/exchanges'
import { captureExchangesEvent } from '@/infrastructure/posthog'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { LoadError, PageHeader } from '@/ui/shared'
import { WarehousesScope } from '@/hooks/use-warehouses-scope'
import {
  FULL_CATALOG_PLATFORMS,
  useFullCatalogShown,
  useScopeHasPlatform
} from '@/hooks/use-scope-platform'
import { useEcommerceId } from '@/hooks/use-ecommerce-id'
import { useStorePlatform } from '@/hooks/use-store-platform'
import { InstantExchangeForm, OrdersForm, PolicyForm } from './exchanges-forms'
import { CatalogSummary, FullCatalogForm } from './full-catalog-form'
import { LayeredSettings } from './layered-settings'

const { Badge, Button, Callout } = Base

export type ExchangesTab = 'general' | 'orders' | 'catalog' | 'instant'

const TAB_KINDS: Record<ExchangesTab, ExchangesKind> = {
  general: 'policy',
  orders: 'orders',
  catalog: 'catalog',
  instant: 'instant'
}

interface ExchangesProps {
  scope: Exclude<WarehousesScope, { kind: 'none' }>
  tab: ExchangesTab
  onShowGroup: () => void
  onShowStore: (ecommerceId: string) => void
}

export const Exchanges: React.FC<ExchangesProps> = ({
  scope,
  tab,
  onShowGroup,
  onShowStore
}) => {
  const { t } = useTranslation()
  const { exchanges, loadingExchanges, exchangesError, reloadExchanges } =
    useGroupExchanges(scope.group)
  const ecommerceId = scope.kind === 'store' ? scope.ecommerceId : undefined
  const viewKey = [tab, scope.group, scope.kind, ecommerceId].join('|')
  const capturedView = useRef<string>()
  // Once per tab and scope, as on Warehouses: the refetch after a save is the same view.
  useEffect(() => {
    if (!exchanges || exchangesError || capturedView.current === viewKey) return
    capturedView.current = viewKey
    const layered = exchanges[TAB_KINDS[tab]]
    captureExchangesEvent('Exchanges viewed', {
      group: scope.group,
      tab,
      level: scope.kind,
      ecommerceId,
      storeCount: scope.stores.length,
      storeOverrideCount: Object.keys(layered.overrides).length,
      hasGroupSettings: !!layered.group
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchanges, exchangesError, viewKey])
  const hasShopify = useScopeHasPlatform(
    PbECommerceSettingsPlatformEnum.Shopify
  )
  const showPaymentMethod = useScopeHasPlatform(
    PbECommerceSettingsPlatformEnum.Prestashop
  )
  const navigate = useNavigate()
  // In the group view, the catalog is searched through a portal of the group, a Shopify one if any: only Shopify can search it.
  const loadedEcommerceId = useEcommerceId()
  const { portals, loadingPortals } = useScopePortals()
  const platformOf = (store?: string) =>
    portals.find((portal) => portal.id === store)?.platform
  const catalogStores = scope.stores.filter((store) =>
    FULL_CATALOG_PLATFORMS.includes(platformOf(store) ?? '')
  )
  const catalogEcommerceId =
    ecommerceId ??
    (loadingPortals
      ? undefined
      : (catalogStores.find(
          (store) =>
            platformOf(store) === PbECommerceSettingsPlatformEnum.Shopify
        ) ??
        catalogStores[0] ??
        loadedEcommerceId))
  // A portal on screen has no portal list loaded: its platform comes with its own settings.
  const storePlatform = useStorePlatform()
  const catalogPlatform = ecommerceId
    ? storePlatform
    : platformOf(catalogEcommerceId)
  const fullCatalogShown = useFullCatalogShown()
  const isShopifyCatalog =
    catalogPlatform === PbECommerceSettingsPlatformEnum.Shopify
  const { titles: extraTitles, loadingTitles } = useExtraDetailsTitles(
    catalogEcommerceId,
    tab === 'catalog' && isShopifyCatalog
  )
  const saveExtraTitles = useSaveExtraDetailsTitles()
  // Payments live in the group-level return conditions only once those are on their flag.
  const { conditions: conditionsLive } = useReturnsAndRefundsV2()
  const fullCatalogPayments = useFullCatalogPayments(scope.group, scope.stores)
  const showDialog = useDialog()
  // A new exchange type changes the returns portal for every shopper.
  const confirmFlowChange = async (
    draft: ExchangePolicy,
    saved: ExchangePolicy
  ) =>
    draft.exchangeFlow === saved.exchangeFlow ||
    showDialog({
      title: t('exchanges.policy.flow_confirm_title'),
      message: t(`exchanges.policy.flow.${draft.exchangeFlow}.shopper`),
      okButtonText: t('exchanges.policy.flow_confirm_ok'),
      cancelButtonText: t('group_settings.cancel')
    })
  const header = (
    <PageHeader
      title={t(`exchanges.heading.${tab}`)}
      description={t(`exchanges.description.${tab}`)}
      actions={
        scope.kind === 'store' ? (
          <Badge tone="info">
            {t('exchanges.scope_store', { store: scope.ecommerceId })}
          </Badge>
        ) : undefined
      }
    />
  )

  if (loadingExchanges) return <ReverLoading />
  // A failed load must not read as a group without settings: saving would replace them with defaults.
  if (exchangesError || !exchanges) {
    return (
      <>
        {header}
        <div className="px-7 py-6">
          <LoadError
            message={t('exchanges.load_error')}
            onRetry={() => reloadExchanges()}
          />
        </div>
      </>
    )
  }
  const shared = { scope, onShowGroup, onShowStore }
  const catalog = ecommerceId
    ? (exchanges.catalog.overrides[ecommerceId] ?? exchanges.catalog.group)
    : exchanges.catalog.group
  const policy =
    (ecommerceId
      ? (exchanges.policy.overrides[ecommerceId] ?? exchanges.policy.group)
      : exchanges.policy.group) ?? DEFAULT_POLICY
  // The extra details title lives in the portal copies, so every layer shows the portal's.
  const withTitles = (layered: Layered<FullCatalog>): Layered<FullCatalog> =>
    isShopifyCatalog
      ? {
          group: layered.group && {
            ...layered.group,
            extraTitles: extraTitles ?? {}
          },
          overrides: Object.fromEntries(
            Object.entries(layered.overrides).map(([store, value]) => [
              store,
              { ...value, extraTitles: extraTitles ?? {} }
            ])
          )
        }
      : layered
  // A group on several stores has no single menu: each portal's tab warns about its own.
  const menuMissing =
    catalogPlatform === PbECommerceSettingsPlatformEnum.Shopify &&
    !catalog?.menuId &&
    (scope.kind === 'store' || !exchanges.multipleStores)
  return (
    <>
      {header}
      <div className="flex max-w-screen-xl flex-col gap-4 px-7 py-4">
        {tab === 'general' ? (
          <LayeredSettings
            key="policy"
            {...shared}
            kind="policy"
            layered={exchanges.policy}
            defaults={DEFAULT_POLICY}
            confirmSave={confirmFlowChange}
            saveAlso={async (draft) => {
              if (conditionsLive && draft.exchangeFlow === 'FULL')
                await fullCatalogPayments.turnPaymentsOn(ecommerceId)
            }}
            render={(props) => (
              <PolicyForm
                {...props}
                fullCatalog={fullCatalogShown}
                catalogNote={
                  fullCatalogShown ? (
                    <>
                      <CatalogNote
                        platform={catalogPlatform}
                        menuMissing={menuMissing}
                        onShowCatalog={() =>
                          navigate('/configuration/exchanges/full-catalog')
                        }
                      />
                      {conditionsLive ? (
                        <p className="text-xs text-neutral-600">
                          {t('exchanges.policy.full_catalog_payments')}
                        </p>
                      ) : null}
                    </>
                  ) : undefined
                }
              />
            )}
          />
        ) : tab === 'orders' ? (
          <LayeredSettings
            key="orders"
            {...shared}
            kind="orders"
            layered={exchanges.orders}
            defaults={DEFAULT_ORDERS}
            render={(props) => (
              <OrdersForm
                {...props}
                showDiscounts={hasShopify}
                showPaymentMethod={showPaymentMethod}
              />
            )}
          />
        ) : tab === 'catalog' ? (
          policy.exchangeFlow !== 'FULL' ? (
            <Callout tone="info" className="text-xs leading-relaxed">
              <Trans
                i18nKey="exchanges.catalog.menu_hidden"
                components={{
                  b: <b />,
                  action: (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      inline
                      onClick={() => navigate('/configuration/exchanges')}
                    />
                  )
                }}
              />
            </Callout>
          ) : scope.kind === 'group' && exchanges.multipleStores ? (
            <PerPortalOnly
              group={scope.group}
              stores={catalogStores}
              onShowStore={onShowStore}
            />
          ) : isShopifyCatalog && loadingTitles ? (
            <ReverLoading />
          ) : (
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
              <div className="min-w-0 flex-1">
                <LayeredSettings
                  key="catalog"
                  {...shared}
                  kind="catalog"
                  layered={withTitles(exchanges.catalog)}
                  defaults={
                    isShopifyCatalog
                      ? {
                          ...DEFAULT_FULL_CATALOG,
                          extraTitles: extraTitles ?? {}
                        }
                      : DEFAULT_FULL_CATALOG
                  }
                  saveAlso={async (draft, saved) => {
                    // Only the languages edited: an emptied title keeps the one the portal shows.
                    const edited = Object.fromEntries(
                      Object.entries(draft.extraTitles ?? {}).filter(
                        ([lang, text]) =>
                          text.trim() && text !== saved.extraTitles?.[lang]
                      )
                    )
                    if (!Object.keys(edited).length) return
                    // A group writes the title of each of its Shopify portals: the copies have no group level.
                    await saveExtraTitles(
                      ecommerceId
                        ? [ecommerceId]
                        : catalogStores.filter(
                            (store) =>
                              platformOf(store) ===
                              PbECommerceSettingsPlatformEnum.Shopify
                          ),
                      edited
                    )
                  }}
                  render={(props) => (
                    <FullCatalogForm
                      {...props}
                      ecommerceId={catalogEcommerceId}
                      platform={catalogPlatform}
                    />
                  )}
                />
              </div>
              <CatalogSummary
                value={catalog ?? DEFAULT_FULL_CATALOG}
                platform={catalogPlatform}
              />
            </div>
          )
        ) : (
          <LayeredSettings
            key="instant"
            {...shared}
            kind="instant"
            layered={exchanges.instant}
            defaults={DEFAULT_INSTANT_EXCHANGE}
            render={(props) => <InstantExchangeForm {...props} />}
          />
        )}
      </div>
    </>
  )
}

// Without a menu, Shopify shoppers get every product of the store in a single list.
function CatalogNote({
  platform,
  menuMissing,
  onShowCatalog
}: {
  platform?: string
  menuMissing: boolean
  onShowCatalog: () => void
}) {
  const link = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      inline
      onClick={onShowCatalog}
    />
  )
  return menuMissing ? (
    <Callout tone="warning" className="text-xs leading-relaxed">
      <Trans
        i18nKey="exchanges.catalog.general_no_menu"
        components={{ action: link }}
      />
    </Callout>
  ) : (
    <p className="text-xs text-neutral-600">
      <Trans
        i18nKey={
          platform === PbECommerceSettingsPlatformEnum.Shopify
            ? 'exchanges.catalog.general_link'
            : 'exchanges.catalog.general_link_exclusions'
        }
        components={{ action: link }}
      />
    </p>
  )
}

// Portals on different stores have different catalogs, so each one sets its own.
function PerPortalOnly({
  group,
  stores,
  onShowStore
}: {
  group: string
  stores: string[]
  onShowStore: (ecommerceId: string) => void
}) {
  return (
    <Callout tone="info" className="text-xs leading-relaxed">
      <div className="flex flex-col gap-2">
        <p className="text-xs">
          <Trans
            i18nKey="exchanges.catalog.per_portal"
            values={{ group }}
            components={{ b: <b /> }}
          />
        </p>
        <div className="flex flex-wrap gap-1">
          {stores.map((store) => (
            <Button
              key={store}
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onShowStore(store)}
            >
              {store}
            </Button>
          ))}
        </div>
      </div>
    </Callout>
  )
}
